import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

export const api = axios.create({
  baseURL: `${BASE}/api`,
  timeout: 180000,
  headers: { "Content-Type": "application/json" },
});

// New canonical multi-chain routes are mounted at the app root, not under /api
const rootApi = axios.create({
  baseURL: BASE,
  timeout: 180000,
  headers: { "Content-Type": "application/json" },
});

export async function scanToken(tokenAddress, chain = "solana") {
  const { data } = await rootApi.get("/scan", {
    params: { token: tokenAddress, chain },
  });
  return data;
}

let _chainsCache = null;
export async function fetchChains() {
  if (_chainsCache) return _chainsCache;
  const { data } = await rootApi.get("/chains");
  _chainsCache = data;
  return data;
}

// ---------------------------------------------------------------------------
// Legacy Solana scan (kept for backward compat — used by existing callers)
// ---------------------------------------------------------------------------
export async function scanMint(mintAddress) {
  const { data } = await api.post("/scan", { mint_address: mintAddress });
  return data;
}

export function bustChainsCache() {
  _chainsCache = null;
}

// ---------------------------------------------------------------------------
// Bundles
// ---------------------------------------------------------------------------
export async function fetchBundles(mintAddress) {
  const { data } = await api.get(`/bundles/${mintAddress}`);
  return data;
}

export async function health() {
  const { data } = await api.get("/health");
  return data;
}

// ---------------------------------------------------------------------------
// Liquidity Bleed Monitor
// ---------------------------------------------------------------------------

export async function fetchWatchlist() {
  const { data } = await api.get("/watchlist");
  return data;
}

export async function addToWatchlist(mintAddress, trackFib = false) {
  const { data } = await api.post("/watchlist", {
    mint_address: mintAddress,
    track_fib: trackFib,
  });
  return data;
}

export async function patchWatchlist(mintAddress, trackFib) {
  const { data } = await api.patch(`/watchlist/${mintAddress}`, {
    track_fib: trackFib,
  });
  return data;
}

export async function removeFromWatchlist(mintAddress) {
  const { data } = await api.delete(`/watchlist/${mintAddress}`);
  return data;
}

// ---------------------------------------------------------------------------
// Watchlist alerts (mute / snooze / log)
// ---------------------------------------------------------------------------

export async function snoozeAlerts(mintAddress, hours = 1) {
  const { data } = await api.post("/alerts/snooze", {
    mint: mintAddress,
    hours,
  });
  return data;
}

export async function muteAlerts(mintAddress, muted) {
  const { data } = await api.post("/alerts/mute", {
    mint: mintAddress,
    muted,
  });
  return data;
}

export async function fetchAlertLog(limit = 50, mint = null) {
  const params = { limit };
  if (mint) params.mint = mint;
  const { data } = await api.get("/alerts/log", { params });
  return data;
}

// ---------------------------------------------------------------------------
// F2 — Positions (manual "in position" tag + exit signals)
// ---------------------------------------------------------------------------

export async function fetchPositions() {
  const { data } = await api.get("/positions");
  return data;
}

export async function openPosition({
  mintAddress,
  entryPrice,
  sizeSol,
  feesBufferPct = 1.5,
  thesis = null,
}) {
  const { data } = await api.post("/positions", {
    mint_address: mintAddress,
    entry_price: entryPrice,
    size_sol: sizeSol,
    fees_buffer_pct: feesBufferPct,
    thesis,
  });
  return data;
}

export async function closePosition(
  positionId,
  { exitPrice = null, reason = "manual", armReentryGuard = true } = {}
) {
  const { data } = await api.post(`/positions/${positionId}/close`, {
    exit_price: exitPrice,
    reason,
    arm_reentry_guard: armReentryGuard,
  });
  return data;
}
